import { useState, useMemo } from 'react';
import { Box, Typography, Paper, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ExpandMore, ExpandLess } from '@material-ui/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkSupersub from 'remark-supersub';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { nord } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { AskResponse, Citation } from '../api';
import { parseAnswer } from './AnswerParser';

const useStyles = makeStyles(theme => ({
  answerContainer: {
    marginBottom: theme.spacing(2),
  },
  answerContent: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    '& p': {
      margin: theme.spacing(1, 0),
    },
    '& pre': {
      backgroundColor: theme.palette.grey[900],
      color: theme.palette.common.white,
      padding: theme.spacing(2),
      borderRadius: theme.shape.borderRadius,
      overflow: 'auto',
    },
    '& code': {
      backgroundColor: theme.palette.grey[200],
      padding: theme.spacing(0.5, 1),
      borderRadius: theme.shape.borderRadius,
      fontFamily: 'monospace',
    },
    '& pre code': {
      backgroundColor: 'transparent',
      padding: 0,
    },
    '& sup': {
      color: theme.palette.primary.main,
      fontWeight: 'bold',
      cursor: 'pointer',
    },
  },
  answerFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
  },
  accordionTitle: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    color: theme.palette.primary.main,
    fontWeight: 500,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  citationWrapper: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
  citationContainer: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
    cursor: 'pointer',
    borderRadius: theme.shape.borderRadius,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  citation: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontSize: '0.75rem',
    fontWeight: 'bold',
    marginRight: theme.spacing(1),
    flexShrink: 0,
  },
  disclaimer: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
}));

type Props = {
  answer: AskResponse;
  onCitationClicked?: (citation: Citation) => void;
};

export const Answer = ({ answer, onCitationClicked }: Props) => {
  const classes = useStyles();
  const [isRefAccordionOpen, setIsRefAccordionOpen] = useState(false);
  const filePathTruncationLimit = 50;

  const parsedAnswer = useMemo(() => parseAnswer(answer), [answer]);

  const createCitationFilepath = (citation: Citation, index: number, truncate: boolean = false): string => {
    let citationFilename = '';

    if (citation.filepath) {
      const part_i = citation.part_index ?? (citation.chunk_id ? parseInt(citation.chunk_id, 10) + 1 : '');
      if (truncate && citation.filepath.length > filePathTruncationLimit) {
        const citationLength = citation.filepath.length;
        citationFilename = `${citation.filepath.substring(0, 20)}...${citation.filepath.substring(citationLength - 20)} - Part ${part_i}`;
      } else {
        citationFilename = `${citation.filepath} - Part ${part_i}`;
      }
    } else if (citation.filepath && citation.reindex_id) {
      citationFilename = `${citation.filepath} - Part ${citation.reindex_id}`;
    } else {
      citationFilename = `Citation ${index}`;
    }
    return citationFilename;
  };

  const markdownComponents = {
    code({ node, inline, className, children, ...props }: any) {
      const match = /language-(\w+)/.exec(className || '');
      const language = match ? match[1] : undefined;
      const codeString = String(children).replace(/\n$/, '');
      
      return !inline && language ? (
        <SyntaxHighlighter style={nord} language={language} PreTag="div" {...props}>
          {codeString}
        </SyntaxHighlighter>
      ) : (
        <code className={className} {...props}>
          {children}
        </code>
      );
    },
  };

  if (!parsedAnswer) {
    return (
      <Box className={classes.answerContainer}>
        <Paper className={classes.answerContent} elevation={1}>
          <Typography>{typeof answer.answer === 'string' ? answer.answer : 'No response'}</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box className={classes.answerContainer}>
      <Paper className={classes.answerContent} elevation={1}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkSupersub]}
          components={markdownComponents}
        >
          {parsedAnswer.markdownFormatText}
        </ReactMarkdown>
      </Paper>

      {parsedAnswer.generated_chart && (
        <Box mt={2}>
          <img 
            src={`data:image/png;base64,${parsedAnswer.generated_chart}`} 
            alt="Generated chart"
            style={{ maxWidth: '100%' }}
          />
        </Box>
      )}

      <Box className={classes.answerFooter}>
        {parsedAnswer.citations.length > 0 && (
          <Box 
            className={classes.accordionTitle}
            onClick={() => setIsRefAccordionOpen(!isRefAccordionOpen)}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                setIsRefAccordionOpen(!isRefAccordionOpen);
              }
            }}
          >
            <Typography variant="body2">
              {parsedAnswer.citations.length > 1
                ? `${parsedAnswer.citations.length} references`
                : '1 reference'}
            </Typography>
            <IconButton size="small">
              {isRefAccordionOpen ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
        )}
        <Typography className={classes.disclaimer}>
          AI-generated content may be incorrect
        </Typography>
      </Box>

      {isRefAccordionOpen && (
        <Box className={classes.citationWrapper}>
          {parsedAnswer.citations.map((citation, idx) => {
            const displayIndex = idx + 1;
            return (
              <Box
                key={citation.id || idx}
                className={classes.citationContainer}
                onClick={() => onCitationClicked?.(citation)}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    onCitationClicked?.(citation);
                  }
                }}
              >
                <Box className={classes.citation}>{displayIndex}</Box>
                <Typography variant="body2">
                  {createCitationFilepath(citation, displayIndex, true)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

